import IconButton from 'components/IconButton';
import { useAtomValue, useSetAtom } from 'jotai';
import { showFailureToast, showSuccessToast } from 'lib/ToastHelper';
import React, { useState } from 'react';
import { CircleSpinner } from 'react-spinners-kit';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { companyIdAtom } from 'views/state';

import ManualScanModal from './ManualScanModal';
import { navigateToBPAFindings } from './navigation';
import UpdateAliasModal from './UpdateAliasModal';
import {
  deleteIntegration,
  manualScan,
} from '../../services/integrationListingService';
import {
  integrationDataRefreshAtom,
  integrationOptionsDataAtom,
  updateIntegrationRowAtom,
} from '../../state';
import {
  ActiveScanItem,
  IntegrationItem,
} from '../../types/integrationListing.types';
import IntegrationDetailsDialog from '../IntegrationDetailsDialog';

interface RowActionsProps {
  row: IntegrationItem;
}

const IntegrationListingRowActions: React.FC<RowActionsProps> = ({ row }) => {
  const companyId = useAtomValue(companyIdAtom);
  const forceReloadIntegrationListing = useSetAtom(integrationDataRefreshAtom);
  const updateIntegrationRow = useSetAtom(updateIntegrationRowAtom);

  const [detailOpen, setDetailOpen] = useState(false);
  const [openAliasModal, setOpenAliasModal] = useState(false);
  const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [scanConfirmModalOpen, setScanConfirmModalOpen] = useState(false);
  const [scanInProgress, setScanInProgress] = useState(false);

  const optionsData = useAtomValue(integrationOptionsDataAtom);

  const handleIntegrationDelete = async (id: number) => {
    setIsDeleting(true);
    await deleteIntegration(id, companyId)
      .then((res) => {
        if (res.status === 204 || res.status === 200) {
          showSuccessToast('Integration deleted successfully.');
        }
      })
      .catch((err) => {
        let error = 'Error occurred. Please try again.';
        if (err?.data?.detail) {
          error = err.data.detail;
        }
        showFailureToast(error);
      })
      .finally(() => {
        setIsDeleting(false);
        setOpenConfirmDeleteModal(false);
        setDetailOpen(false);
        forceReloadIntegrationListing(Math.random().toString());
      });
  };

  const handleManualScan = async (
    region: string,
    benchmarkValues: string[],
    benchmarkNames: string[],
  ) => {
    setScanInProgress(true);
    await manualScan(row.id, companyId, region, benchmarkValues)
      .then((res) => {
        if (res.status === 201 || res.status === 200) {
          showSuccessToast('Manual scan initiated successfully.');
          const updatedActiveScans: ActiveScanItem[] = res.data
            ?.active_scans ?? [
            ...(row.active_scans || []),
            ...benchmarkNames.map((name) => ({
              benchmark: name,
              region,
              started_at: new Date().toISOString(),
            })),
          ];
          updateIntegrationRow({
            id: row.id,
            data: {
              active_scans: updatedActiveScans,
              active_scan_count: updatedActiveScans.length,
            } as Partial<IntegrationItem>,
          });
        } else {
          showFailureToast('Failed to launch manual scan.');
        }
      })
      .catch((_err) => {
        if (_err?.data?.error && _err.status !== 500) {
          showFailureToast(`Failed to launch manual scan: ${_err.data.error}`);
        } else {
          showFailureToast(
            'Failed to launch manual scan. SP360 is notified about the issue.',
          );
        }
      })
      .finally(() => {
        setScanInProgress(false);
      });
  };

  return (
    <div className="text-nowrap d-flex gap-2 align-items-center justify-content-end">
      {!row.is_deleted && (
        <>
          <IconButton
            id={`editAlias-${row.id}`}
            iconClass="fa fa-edit"
            size="sm"
            tooltip="Edit integration name"
            onClick={() => {
              setOpenAliasModal(true);
            }}
            disabled={row.is_active === false}
          />

          {scanInProgress ? (
            <IconButton
              id={`manualScanLink-${row.id}`}
              size="sm"
              tooltip="Scan is in progress..."
              iconElement={
                <CircleSpinner size={20} color="#0665a9" loading={true} />
              }
              onClick={() => {}}
              disabled
            />
          ) : (
            <IconButton
              id={`manualScanLink-${row.id}`}
              iconClass="fa fa-search"
              size="sm"
              tooltip={
                row.is_active === false
                  ? 'Scan disabled for inactive integration'
                  : 'Click to initiate scan'
              }
              onClick={() => {
                setScanConfirmModalOpen(true);
              }}
              disabled={row.is_active === false}
            />
          )}

          <IconButton
            id={`viewFindingsLink-${row.id}`}
            iconClass="fa fa-clipboard-list"
            size="sm"
            tooltip={
              (row.active_finding_count ?? 0) > 0
                ? 'View Active Findings'
                : 'No Active Findings Available'
            }
            onClick={() => navigateToBPAFindings(row.id, row.integration_label)}
            disabled={(row.active_finding_count ?? 0) === 0}
          />
        </>
      )}

      <IconButton
        id={`detailsLink-${row.id}`}
        iconClass="fa fa-eye"
        size="sm"
        tooltip="View Integration Details"
        onClick={() => setDetailOpen(true)}
      />

      <IntegrationDetailsDialog
        integrationId={row.id}
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
      />

      {openConfirmDeleteModal && (
        <Modal isOpen={openConfirmDeleteModal} size="sm">
          <ModalHeader>
            <h4>Delete Confirmation</h4>
          </ModalHeader>
          <ModalBody>
            <p>Are you sure you want to delete this Integration?</p>
            <label style={{ color: 'dodgerblue' }}>
              Note: This operation is <strong>irreversible</strong>.
            </label>
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              onClick={() => setOpenConfirmDeleteModal(false)}
            >
              Cancel
            </Button>
            {isDeleting ? (
              <CircleSpinner size={20} color="#0665a9" loading={true} />
            ) : (
              <Button
                color="danger"
                disabled={isDeleting}
                onClick={() => handleIntegrationDelete(row.id)}
              >
                Delete
              </Button>
            )}
          </ModalFooter>
        </Modal>
      )}

      {scanConfirmModalOpen && (
        <ManualScanModal
          isOpen={scanConfirmModalOpen}
          toggle={() => setScanConfirmModalOpen(false)}
          row={row}
          optionsData={optionsData}
          onConfirm={handleManualScan}
        />
      )}

      {openAliasModal && (
        <UpdateAliasModal
          isOpen={openAliasModal}
          toggle={() => setOpenAliasModal(false)}
          row={row}
        />
      )}
    </div>
  );
};

export default IntegrationListingRowActions;
